import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UserInfo {
  name?: string;
  email?: string;
  phone?: string;
  pre_session_fields?: Record<string, any>;
}

export const usePublicAgentAuth = (
  publicId: string | undefined,
  isEmbedded: boolean
) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [agentChatUserId, setAgentChatUserId] = useState<string | null>(null);
  const [agentData, setAgentData] = useState<any>(null);
  const [requirePhoneNumber, setRequirePhoneNumber] = useState<boolean>(false);
  const [needsReauthentication, setNeedsReauthentication] = useState<boolean>(false);

  // First, fetch the agent to check if phone is required
  useEffect(() => {
    const fetchAgent = async () => {
      if (!publicId) return;

      try {
        const { data, error } = await supabase
          .from("agents")
          .select("contact_info")
          .eq("public_id", publicId)
          .single();

        if (error) {
          console.error("Error fetching agent:", error);
          return;
        }

        setAgentData(data);
        
        // Check if phone number is required
        const contactInfo = data?.contact_info as Record<string, unknown> || {};
        setRequirePhoneNumber(contactInfo.requirePhoneNumber === true);
        console.log("Phone number required:", contactInfo.requirePhoneNumber === true);
      } catch (error) {
        console.error("Error in fetchAgent:", error);
      }
    };

    fetchAgent();
  }, [publicId]);

  // This effect depends on requirePhoneNumber being set from the previous effect
  useEffect(() => {
    if (!publicId) return;

    // Check if user info is available in localStorage
    const savedUserData = localStorage.getItem(`agent_chat_user_${publicId}`);

    if (savedUserData) {
      try {
        const parsedUserData = JSON.parse(savedUserData);
        console.log("Found saved user data:", parsedUserData);
        
        // Ensure pre_session_fields exists and is an object
        if (!parsedUserData.pre_session_fields || typeof parsedUserData.pre_session_fields !== 'object') {
          parsedUserData.pre_session_fields = {}; // Initialize if missing or invalid
        }
        
        // Check if phone is required but missing from saved data
        if (requirePhoneNumber && !parsedUserData.phone) {
          console.log("Phone required but not found in saved data. Will need to reauthenticate.");
          setNeedsReauthentication(true);
          // Don't set userInfo to force reauthentication
        } else {
          setUserInfo(parsedUserData);
        }

        // If we have a saved agent_chat_user_id, set that too
        const savedUserId = localStorage.getItem(
          `agent_chat_user_id_${publicId}`
        );
        if (savedUserId) {
          setAgentChatUserId(savedUserId);
        }
      } catch (error) {
        console.error("Error parsing saved user data:", error);
      }
    }
  }, [publicId, requirePhoneNumber]);

  // Determine if authentication is required
  // If phone is required but not provided, consider user as not authenticated
  const needsAuth = !isEmbedded && 
    (!userInfo || needsReauthentication);

  // Add a function to save user info
  const saveUserInfo = async (data: UserInfo) => {
    if (!publicId) return false;

    // If phone is required but not provided, reject the save
    if (requirePhoneNumber && !data.phone) {
      console.error("Phone number is required but not provided");
      return false;
    }

    try {
      // Save to state - clear reauthentication flag
      setUserInfo(data);
      setNeedsReauthentication(false);

      // Create a consistent storage object that preserves pre_session_fields
      const storageObject = {
        name: data.name || "Anonymous User",
        email: data.email || null,
        phone: data.phone || null,
        // Ensure pre_session_fields are included and properly copied
        pre_session_fields: data.pre_session_fields ? JSON.parse(JSON.stringify(data.pre_session_fields)) : {}
      };
      
      // Save to localStorage with consistent structure
      localStorage.setItem(`agent_chat_user_${publicId}`, JSON.stringify(storageObject));
      
      // Save to Supabase using the same object structure to ensure consistency
      let userId = agentChatUserId;

      if (!userId) {
        // Create a new agent_chat_user record
        const insertData = {
          name: storageObject.name,
          email: storageObject.email,
          phone: storageObject.phone,
          is_anonymous: true,
          pre_session_fields: storageObject.pre_session_fields,
          created_by_email: storageObject.email
        };
        
        const { data: newUser, error } = await supabase
          .from("agent_chat_users")
          .insert(insertData)
          .select()
          .single();

        if (error) {
          console.error("Error creating agent_chat_user:", error);
          return false;
        }

        userId = newUser.id;
        setAgentChatUserId(userId);
        localStorage.setItem(`agent_chat_user_id_${publicId}`, userId);
        
        // Update localStorage with database values for consistency
        if (newUser) {
          const updatedStorage = {
            name: newUser.name || storageObject.name,
            email: newUser.email || storageObject.email,
            phone: newUser.phone || storageObject.phone,
            pre_session_fields: (newUser as any).pre_session_fields || storageObject.pre_session_fields
          };
          
          localStorage.setItem(`agent_chat_user_${publicId}`, JSON.stringify(updatedStorage));
        }
      } else {
        // Update existing agent_chat_user record
        const updatePayload = {
          name: storageObject.name,
          email: storageObject.email,
          phone: storageObject.phone,
          updated_at: new Date().toISOString()
        };
        
        // Only include pre_session_fields in the payload if it has actual content
        // This prevents overwriting with an empty object
        if (storageObject.pre_session_fields && 
            typeof storageObject.pre_session_fields === 'object' && 
            Object.keys(storageObject.pre_session_fields).length > 0) {
          updatePayload.pre_session_fields = storageObject.pre_session_fields;
        }
        
        const { data: updatedUser, error } = await supabase
          .from("agent_chat_users")
          .update(updatePayload)
          .eq("id", userId)
          .select()
          .single();

        if (error) {
          console.error("Error updating agent_chat_user:", error);
          return false;
        }
        
        // Update localStorage with database values for consistency
        if (updatedUser) {
          const updatedStorage = {
            name: updatedUser.name || storageObject.name,
            email: updatedUser.email || storageObject.email,
            phone: updatedUser.phone || storageObject.phone,
            pre_session_fields: (updatedUser as any).pre_session_fields || storageObject.pre_session_fields
          };
          
          localStorage.setItem(`agent_chat_user_${publicId}`, JSON.stringify(updatedStorage));
        }
      }

      return true;
    } catch (error) {
      console.error("Error in saveUserInfo:", error);
      return false;
    }
  };

  return { 
    userInfo, 
    needsAuth, 
    saveUserInfo, 
    agentChatUserId,
    requirePhoneNumber
  };
};
